import { RiEditBoxLine } from "react-icons/ri"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { Link } from "react-router-dom"
import { formattedDate } from "../../../utils/dateFormatter"
import { User, Mail, Calendar, Phone, FileText, Edit3 } from "lucide-react"

export default function MyProfile() {
  const { user } = useSelector((state) => state.profile)
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Modern Header */}
        <div className="mb-2">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-xl">
              <User className="h-6 w-6 text-white" />
            </div>
          
          <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent ">
            My Profile
          </h1>
          </div>
          <p className="text-slate-600 text-lg">Manage your personal information and settings</p>
        </div>

        {/* Profile Card */}
        <div className="relative overflow-hidden mb-8">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-indigo-600/5 rounded-2xl"></div>
          <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="relative group">
                  <img
                    src={user?.image}
                    alt={`profile-${user?.firstName}`}
                    //view enlarge image
                    
                    className="w-24 h-24 rounded-full object-cover cursor-pointer border-4 border-white shadow-xl transition-transform duration-300 group-hover:scale-105"
                  />
                  <div 
                  onClick={() => window.open(user?.image, "_blank")}
                  className="absolute cursor-pointer inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <div className="text-center md:text-left">
                  <h2 className="text-2xl font-bold text-slate-800 mb-2">
                    {user?.firstName + " " + user?.lastName}
                  </h2>
                  <div className="flex items-center gap-2 text-slate-600 justify-center md:justify-start">
                    <Mail className="h-4 w-4" />
                    <span>{user?.email}</span>
                  </div>
                  <div className="mt-2 px-3 py-1 bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700 rounded-full text-sm font-medium w-fit mx-auto md:mx-0">
                    {user?.accountType}
                  </div>
                </div>
              </div>
              
              <Link to="/dashboard/settings">
                <button className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 transform hover:scale-105 font-semibold">
                  <Edit3 className="h-5 w-5" />
                  Edit Profile
                </button>
              </Link>
            </div>
          </div>
        </div>

        {/* About Section */}
        <div className="relative overflow-hidden mb-8">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-green-600/5 rounded-2xl"></div>
          <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl shadow-lg">
                  <FileText className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-800">About</h3>
              </div>
              <Link to="/dashboard/settings">
                <button className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white p-2 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                  <RiEditBoxLine className="h-5 w-5" />
                </button>
              </Link>
            </div>
            <p className={`text-slate-700 leading-relaxed ${
              user?.additionalDetails?.about ? "" : "italic text-slate-500"
            }`}>
              {user?.additionalDetails?.about ?? "Write something about yourself to let others know more about you."}
            </p>
          </div>
        </div>

        {/* Personal Details */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-indigo-600/5 rounded-2xl"></div>
          <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-8">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl shadow-lg">
                  <User className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-800">Personal Details</h3>
              </div>
              <Link to="/dashboard/settings">
                <button className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white p-2 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                  <RiEditBoxLine className="h-5 w-5" />
                </button>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="p-4 bg-blue-50/80 backdrop-blur-sm rounded-xl border border-blue-200/50">
                  <p className="text-sm font-semibold text-blue-700 uppercase tracking-wider mb-2">First Name</p>
                  <p className="text-slate-800 font-medium">{user?.firstName}</p>
                </div>

                <div className="p-4 bg-emerald-50/80 backdrop-blur-sm rounded-xl border border-emerald-200/50">
                  <div className="flex items-center gap-2 mb-2">
                    <Mail className="h-4 w-4 text-emerald-600" />
                    <p className="text-sm font-semibold text-emerald-700 uppercase tracking-wider">Email</p>
                  </div>
                  <p className="text-slate-800 font-medium">{user?.email}</p>
                </div>

                <div className="p-4 bg-purple-50/80 backdrop-blur-sm rounded-xl border border-purple-200/50">
                  <p className="text-sm font-semibold text-purple-700 uppercase tracking-wider mb-2">Gender</p>
                  <p className="text-slate-800 font-medium">
                    {user?.additionalDetails?.gender ?? "Not specified"}
                  </p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="p-4 bg-indigo-50/80 backdrop-blur-sm rounded-xl border border-indigo-200/50">
                  <p className="text-sm font-semibold text-indigo-700 uppercase tracking-wider mb-2">Last Name</p>
                  <p className="text-slate-800 font-medium">{user?.lastName}</p>
                </div>

                <div className="p-4 bg-amber-50/80 backdrop-blur-sm rounded-xl border border-amber-200/50">
                  <div className="flex items-center gap-2 mb-2">
                    <Phone className="h-4 w-4 text-amber-600" />
                    <p className="text-sm font-semibold text-amber-700 uppercase tracking-wider">Phone Number</p>
                  </div>
                  <p className="text-slate-800 font-medium">
                    {user?.additionalDetails?.contactNumber ?? "Not provided"}
                  </p>
                </div>

                <div className="p-4 bg-pink-50/80 backdrop-blur-sm rounded-xl border border-pink-200/50">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="h-4 w-4 text-pink-600" />
                    <p className="text-sm font-semibold text-pink-700 uppercase tracking-wider">Date of Birth</p>
                  </div>
                  <p className="text-slate-800 font-medium">
                    {formattedDate(user?.additionalDetails?.dateOfBirth) ?? "Not provided"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}